import {
  HeartHandshake,
  Camera,
  Wrench,
  MessageCircle,
  TrendingUp,
  Eye,
  MapPin,
  Sparkles,
} from "lucide-react";

const PILLARS = [
  {
    icon: HeartHandshake,
    title: "Suivi humain réel",
    desc: "Un seul City Manager dédié connaît votre logement, vos voisins, vos spécificités.",
  },
  {
    icon: Camera,
    title: "Photos après chaque ménage",
    desc: "Preuve photographique systématique après le passage de nos ménagères.",
  },
  {
    icon: Wrench,
    title: "Service technique rapide",
    desc: "Intervention physique sous 30-60 min sur la Côte d'Émeraude.",
  },
  {
    icon: MessageCircle,
    title: "Voyageurs 7j/7, 24h/24",
    desc: "Permanence de jour ET de nuit. Aucun message n'attend plus de 30 minutes.",
  },
  {
    icon: TrendingUp,
    title: "Pricing dynamique optimisé",
    desc: "Tarif ajusté chaque jour selon météo, événements et saisonnalité.",
  },
  {
    icon: Eye,
    title: "Transparence totale",
    desc: "Espace propriétaire en temps réel : revenus, calendrier, durée.",
  },
  {
    icon: MapPin,
    title: "Entreprise 100 % bretonne",
    desc: "Pas une franchise nationale. Nos équipes vivent ici, sur la côte.",
  },
  {
    icon: Sparkles,
    title: "70 logements pas plus",
    desc: "Notre engagement : refuser la logique d'usine pour préserver la qualité.",
  },
];

const WhyTheyStay = () => (
  <section
    className="py-20 lg:py-28 bg-cream relative overflow-hidden"
    data-testid="why-they-stay"
  >
    {/* Soft gold orb top-right */}
    <div
      aria-hidden="true"
      className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full pointer-events-none opacity-50"
      style={{
        background: "radial-gradient(circle, rgba(197,138,63,0.18) 0%, transparent 65%)",
      }}
    />
    {/* Soft sage orb bottom-left */}
    <div
      aria-hidden="true"
      className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full pointer-events-none opacity-50"
      style={{
        background: "radial-gradient(circle, rgba(122,154,130,0.16) 0%, transparent 65%)",
      }}
    />

    <div className="container-tm relative">
      {/* === HEADER : editorial-style 2 columns asymmetric === */}
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-16 lg:mb-20 items-center">
        {/* Left : Big stat */}
        <div className="lg:col-span-5">
          <div className="relative inline-block">
            <p className="overline mb-3">Rétention propriétaires</p>
            <div className="relative">
              <span
                className="font-serif italic font-light text-ink leading-none tracking-tight"
                style={{ fontSize: "clamp(96px, 16vw, 200px)" }}
              >
                94
              </span>
              <span className="absolute top-3 -right-9 text-3xl text-gold font-serif italic">
                %
              </span>
            </div>
            <p className="font-sans text-ink/60 text-sm mt-2 max-w-[24ch]">
              de nos propriétaires sont toujours avec nous après 12 mois.
            </p>
          </div>
        </div>

        {/* Right : Title + intro */}
        <div className="lg:col-span-7">
          <h2 className="font-serif font-light text-ink text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-tight">
            Pourquoi les propriétaires
            <br />
            <em className="text-gold">nous quittent rarement.</em>
          </h2>
          <p className="mt-6 font-sans text-ink/65 text-base md:text-lg leading-relaxed max-w-2xl">
            8 raisons concrètes, pas du marketing, qui font qu'une fois
            confiés à Terre et Mer, nos propriétaires restent des années. Et
            nous recommandent à leurs voisins.
          </p>
        </div>
      </div>

      {/* === MANIFESTO : full-width hero block === */}
      <div
        className="relative max-w-5xl mx-auto mb-16 lg:mb-20 px-6 md:px-12 py-12 md:py-14 rounded-[2.5rem] bg-ink text-cream overflow-hidden"
        data-testid="why-stay-manifesto-block"
      >
        {/* Decorative quote mark */}
        <span
          aria-hidden="true"
          className="absolute -top-10 left-6 md:left-12 font-serif text-gold/15 select-none"
          style={{ fontSize: "200px", lineHeight: 1 }}
        >
          “
        </span>

        <div className="relative">
          <div className="inline-flex items-center gap-2 mb-5">
            <span className="h-px w-8 bg-gold/60" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-gold font-semibold">
              Notre manifeste
            </span>
          </div>
          <blockquote
            className="font-serif italic font-light text-cream text-2xl md:text-3xl lg:text-4xl leading-tight tracking-tight"
            data-testid="why-stay-manifesto"
          >
            Nous ne cherchons pas à avoir{" "}
            <span className="line-through text-cream/30 not-italic">
              500 logements
            </span>
            .{" "}
            <em className="text-gold not-italic">
              Nous préférons suivre correctement chaque bien.
            </em>
          </blockquote>
          <p className="mt-6 text-cream/45 text-xs uppercase tracking-[0.22em]">
           , L'équipe Terre et Mer Conciergerie · Saint-Malo & Marzan
          </p>
        </div>
      </div>

      {/* === 8 REASONS : editorial numbered list, 2 columns === */}
      <div className="max-w-6xl mx-auto">
        <p className="text-center text-[10px] uppercase tracking-[0.32em] text-ink/45 font-semibold mb-10">
          ⎯⎯ Les 8 raisons concrètes ⎯⎯
        </p>
        <div className="grid md:grid-cols-2 gap-y-2 gap-x-12 lg:gap-x-20">
          {PILLARS.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                data-testid={`why-stay-pillar-${i}`}
                className="group relative flex items-start gap-5 py-5 border-b border-ink/10 hover:border-gold/40 transition-colors duration-500"
              >
                {/* Number */}
                <span className="font-serif italic text-gold/70 text-2xl font-light leading-none shrink-0 w-10 mt-0.5 group-hover:text-gold transition-colors duration-500">
                  0{i + 1}
                </span>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2.5 mb-1.5">
                    <Icon
                      size={16}
                      strokeWidth={1.6}
                      className="text-gold/80 shrink-0"
                    />
                    <h3 className="font-serif text-lg text-ink leading-snug">
                      {p.title}
                    </h3>
                  </div>
                  <p className="font-sans text-[14px] text-ink/65 leading-relaxed">
                    {p.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export default WhyTheyStay;
